/**
 * Measurement Converter — grams ↔ cups / ounces / teaspoons
 *
 * Comprehensive conversion database for cooking ingredients.
 * When a specific ingredient density is unknown, generic ratios are used.
 */

export type MeasureUnit = "metric" | "cups" | "ounces";

interface ConversionEntry {
  /** grams per cup */
  gramsPerCup: number;
  /** grams per tablespoon (for small amounts) */
  gramsPerTbsp: number;
}

// ─── Ingredient-specific densities (grams per cup / per tbsp) ───
const INGREDIENT_DENSITIES: Record<string, ConversionEntry> = {
  // ── Flours & starches ──
  harina:                { gramsPerCup: 125, gramsPerTbsp: 8 },
  flour:                 { gramsPerCup: 125, gramsPerTbsp: 8 },
  "harina de almendra":  { gramsPerCup: 96,  gramsPerTbsp: 6 },
  "almond flour":        { gramsPerCup: 96,  gramsPerTbsp: 6 },
  "harina de coco":      { gramsPerCup: 112, gramsPerTbsp: 7 },
  "coconut flour":       { gramsPerCup: 112, gramsPerTbsp: 7 },
  "harina integral":     { gramsPerCup: 130, gramsPerTbsp: 8 },
  "whole wheat flour":   { gramsPerCup: 130, gramsPerTbsp: 8 },
  "harina de avena":     { gramsPerCup: 100, gramsPerTbsp: 6.5 },
  "oat flour":           { gramsPerCup: 100, gramsPerTbsp: 6.5 },
  "harina de arroz":     { gramsPerCup: 158, gramsPerTbsp: 10 },
  "rice flour":          { gramsPerCup: 158, gramsPerTbsp: 10 },
  maicena:               { gramsPerCup: 128, gramsPerTbsp: 8 },
  "fécula de maíz":      { gramsPerCup: 128, gramsPerTbsp: 8 },
  cornstarch:            { gramsPerCup: 128, gramsPerTbsp: 8 },
  almidón:               { gramsPerCup: 128, gramsPerTbsp: 8 },
  "harina de garbanzo":  { gramsPerCup: 90,  gramsPerTbsp: 6 },
  "chickpea flour":      { gramsPerCup: 90,  gramsPerTbsp: 6 },

  // ── Oats & cereals ──
  avena:                 { gramsPerCup: 90,  gramsPerTbsp: 6 },
  oats:                  { gramsPerCup: 90,  gramsPerTbsp: 6 },
  granola:               { gramsPerCup: 110, gramsPerTbsp: 7 },

  // ── Sugars & sweeteners ──
  azúcar:                { gramsPerCup: 200, gramsPerTbsp: 12.5 },
  azucar:                { gramsPerCup: 200, gramsPerTbsp: 12.5 },
  sugar:                 { gramsPerCup: 200, gramsPerTbsp: 12.5 },
  "azúcar glass":        { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  "powdered sugar":      { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  "azúcar morena":       { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "brown sugar":         { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "azúcar mascabado":    { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  piloncillo:            { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  eritritol:             { gramsPerCup: 180, gramsPerTbsp: 11 },
  erythritol:            { gramsPerCup: 180, gramsPerTbsp: 11 },
  stevia:                { gramsPerCup: 24,  gramsPerTbsp: 1.5 },
  estevia:               { gramsPerCup: 24,  gramsPerTbsp: 1.5 },
  xilitol:               { gramsPerCup: 190, gramsPerTbsp: 12 },
  xylitol:               { gramsPerCup: 190, gramsPerTbsp: 12 },
  "monk fruit":          { gramsPerCup: 150, gramsPerTbsp: 9.5 },
  "fruto del monje":     { gramsPerCup: 150, gramsPerTbsp: 9.5 },
  miel:                  { gramsPerCup: 340, gramsPerTbsp: 21 },
  honey:                 { gramsPerCup: 340, gramsPerTbsp: 21 },
  "jarabe de arce":      { gramsPerCup: 312, gramsPerTbsp: 20 },
  "maple syrup":         { gramsPerCup: 312, gramsPerTbsp: 20 },
  "jarabe de agave":     { gramsPerCup: 336, gramsPerTbsp: 21 },
  agave:                 { gramsPerCup: 336, gramsPerTbsp: 21 },
  melaza:                { gramsPerCup: 340, gramsPerTbsp: 21 },
  molasses:              { gramsPerCup: 340, gramsPerTbsp: 21 },

  // ── Fats & oils ──
  mantequilla:           { gramsPerCup: 227, gramsPerTbsp: 14 },
  butter:                { gramsPerCup: 227, gramsPerTbsp: 14 },
  margarina:             { gramsPerCup: 227, gramsPerTbsp: 14 },
  margarine:             { gramsPerCup: 227, gramsPerTbsp: 14 },
  aceite:                { gramsPerCup: 218, gramsPerTbsp: 14 },
  oil:                   { gramsPerCup: 218, gramsPerTbsp: 14 },
  "aceite de oliva":     { gramsPerCup: 216, gramsPerTbsp: 13.5 },
  "olive oil":           { gramsPerCup: 216, gramsPerTbsp: 13.5 },
  "aceite de coco":      { gramsPerCup: 218, gramsPerTbsp: 14 },
  "coconut oil":         { gramsPerCup: 218, gramsPerTbsp: 14 },
  manteca:               { gramsPerCup: 205, gramsPerTbsp: 13 },
  lard:                  { gramsPerCup: 205, gramsPerTbsp: 13 },
  ghee:                  { gramsPerCup: 218, gramsPerTbsp: 14 },

  // ── Dairy & alt milks ──
  leche:                 { gramsPerCup: 244, gramsPerTbsp: 15 },
  milk:                  { gramsPerCup: 244, gramsPerTbsp: 15 },
  "leche de almendra":   { gramsPerCup: 240, gramsPerTbsp: 15 },
  "almond milk":         { gramsPerCup: 240, gramsPerTbsp: 15 },
  "leche de coco":       { gramsPerCup: 240, gramsPerTbsp: 15 },
  "coconut milk":        { gramsPerCup: 240, gramsPerTbsp: 15 },
  "leche de avena":      { gramsPerCup: 240, gramsPerTbsp: 15 },
  "oat milk":            { gramsPerCup: 240, gramsPerTbsp: 15 },
  "leche evaporada":     { gramsPerCup: 252, gramsPerTbsp: 16 },
  "evaporated milk":     { gramsPerCup: 252, gramsPerTbsp: 16 },
  "leche condensada":    { gramsPerCup: 306, gramsPerTbsp: 19 },
  "condensed milk":      { gramsPerCup: 306, gramsPerTbsp: 19 },
  crema:                 { gramsPerCup: 240, gramsPerTbsp: 15 },
  cream:                 { gramsPerCup: 240, gramsPerTbsp: 15 },
  "crema agria":         { gramsPerCup: 230, gramsPerTbsp: 14.5 },
  "sour cream":          { gramsPerCup: 230, gramsPerTbsp: 14.5 },
  yogur:                 { gramsPerCup: 245, gramsPerTbsp: 15 },
  yogurt:                { gramsPerCup: 245, gramsPerTbsp: 15 },
  "queso crema":         { gramsPerCup: 232, gramsPerTbsp: 14.5 },
  "cream cheese":        { gramsPerCup: 232, gramsPerTbsp: 14.5 },
  queso:                 { gramsPerCup: 113, gramsPerTbsp: 7 },
  cheese:                { gramsPerCup: 113, gramsPerTbsp: 7 },
  ricotta:               { gramsPerCup: 246, gramsPerTbsp: 15 },
  mascarpone:            { gramsPerCup: 227, gramsPerTbsp: 14 },

  // ── Cocoa & chocolate ──
  cacao:                 { gramsPerCup: 86,  gramsPerTbsp: 5.4 },
  cocoa:                 { gramsPerCup: 86,  gramsPerTbsp: 5.4 },
  "cocoa powder":        { gramsPerCup: 86,  gramsPerTbsp: 5.4 },
  "chocolate chips":     { gramsPerCup: 170, gramsPerTbsp: 10.6 },
  "chispas de chocolate":{ gramsPerCup: 170, gramsPerTbsp: 10.6 },
  chocolate:             { gramsPerCup: 170, gramsPerTbsp: 10.6 },

  // ── Nuts & seeds ──
  nueces:                { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  walnuts:               { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  almendras:             { gramsPerCup: 143, gramsPerTbsp: 9 },
  almonds:               { gramsPerCup: 143, gramsPerTbsp: 9 },
  cacahuate:             { gramsPerCup: 146, gramsPerTbsp: 9 },
  cacahuates:            { gramsPerCup: 146, gramsPerTbsp: 9 },
  peanuts:               { gramsPerCup: 146, gramsPerTbsp: 9 },
  maní:                  { gramsPerCup: 146, gramsPerTbsp: 9 },
  pistachos:             { gramsPerCup: 123, gramsPerTbsp: 7.7 },
  pistachios:            { gramsPerCup: 123, gramsPerTbsp: 7.7 },
  avellanas:             { gramsPerCup: 135, gramsPerTbsp: 8.4 },
  hazelnuts:             { gramsPerCup: 135, gramsPerTbsp: 8.4 },
  pecanas:               { gramsPerCup: 109, gramsPerTbsp: 6.8 },
  pecans:                { gramsPerCup: 109, gramsPerTbsp: 6.8 },
  "nuez de castilla":    { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  "nuez de la india":    { gramsPerCup: 137, gramsPerTbsp: 8.5 },
  cashews:               { gramsPerCup: 137, gramsPerTbsp: 8.5 },
  "nuez de macadamia":   { gramsPerCup: 134, gramsPerTbsp: 8.4 },
  macadamia:             { gramsPerCup: 134, gramsPerTbsp: 8.4 },
  "semillas de girasol": { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  "sunflower seeds":     { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  "semillas de calabaza":{ gramsPerCup: 129, gramsPerTbsp: 8 },
  "pumpkin seeds":       { gramsPerCup: 129, gramsPerTbsp: 8 },
  pepitas:               { gramsPerCup: 129, gramsPerTbsp: 8 },
  "pine nuts":           { gramsPerCup: 135, gramsPerTbsp: 8.5 },
  piñones:               { gramsPerCup: 135, gramsPerTbsp: 8.5 },

  // ── Seeds (small) ──
  chía:                  { gramsPerCup: 168, gramsPerTbsp: 10.5 },
  chia:                  { gramsPerCup: 168, gramsPerTbsp: 10.5 },
  linaza:                { gramsPerCup: 155, gramsPerTbsp: 10 },
  linseed:               { gramsPerCup: 155, gramsPerTbsp: 10 },
  flaxseed:              { gramsPerCup: 155, gramsPerTbsp: 10 },
  "semillas de lino":    { gramsPerCup: 155, gramsPerTbsp: 10 },
  ajonjolí:              { gramsPerCup: 144, gramsPerTbsp: 9 },
  "sesame seeds":        { gramsPerCup: 144, gramsPerTbsp: 9 },
  sésamo:                { gramsPerCup: 144, gramsPerTbsp: 9 },
  amapola:               { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  "poppy seeds":         { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  hemp:                  { gramsPerCup: 160, gramsPerTbsp: 10 },
  "semillas de cáñamo":  { gramsPerCup: 160, gramsPerTbsp: 10 },

  // ── Nut & seed butters ──
  "crema de cacahuate":  { gramsPerCup: 258, gramsPerTbsp: 16 },
  "peanut butter":       { gramsPerCup: 258, gramsPerTbsp: 16 },
  "crema de almendra":   { gramsPerCup: 256, gramsPerTbsp: 16 },
  "almond butter":       { gramsPerCup: 256, gramsPerTbsp: 16 },
  tahini:                { gramsPerCup: 240, gramsPerTbsp: 15 },

  // ── Grains & legumes ──
  arroz:                 { gramsPerCup: 185, gramsPerTbsp: 12 },
  rice:                  { gramsPerCup: 185, gramsPerTbsp: 12 },
  quinoa:                { gramsPerCup: 170, gramsPerTbsp: 10.6 },
  quinua:                { gramsPerCup: 170, gramsPerTbsp: 10.6 },
  cuscús:                { gramsPerCup: 173, gramsPerTbsp: 10.8 },
  couscous:              { gramsPerCup: 173, gramsPerTbsp: 10.8 },
  lenteja:               { gramsPerCup: 192, gramsPerTbsp: 12 },
  lentejas:              { gramsPerCup: 192, gramsPerTbsp: 12 },
  lentils:               { gramsPerCup: 192, gramsPerTbsp: 12 },
  frijol:                { gramsPerCup: 184, gramsPerTbsp: 11.5 },
  frijoles:              { gramsPerCup: 184, gramsPerTbsp: 11.5 },
  beans:                 { gramsPerCup: 184, gramsPerTbsp: 11.5 },
  garbanzo:              { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  garbanzos:             { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  chickpeas:             { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  "chícharos":           { gramsPerCup: 160, gramsPerTbsp: 10 },
  peas:                  { gramsPerCup: 160, gramsPerTbsp: 10 },
  amaranto:              { gramsPerCup: 195, gramsPerTbsp: 12.2 },
  amaranth:              { gramsPerCup: 195, gramsPerTbsp: 12.2 },
  trigo:                 { gramsPerCup: 180, gramsPerTbsp: 11.3 },

  // ── Dried fruits ──
  pasas:                 { gramsPerCup: 145, gramsPerTbsp: 9 },
  raisins:               { gramsPerCup: 145, gramsPerTbsp: 9 },
  arándanos:             { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  cranberries:           { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  dátiles:               { gramsPerCup: 147, gramsPerTbsp: 9.2 },
  dates:                 { gramsPerCup: 147, gramsPerTbsp: 9.2 },
  "coco rallado":        { gramsPerCup: 85,  gramsPerTbsp: 5.3 },
  "shredded coconut":    { gramsPerCup: 85,  gramsPerTbsp: 5.3 },
  coco:                  { gramsPerCup: 85,  gramsPerTbsp: 5.3 },

  // ── Fresh produce (common weighed items) ──
  espinaca:              { gramsPerCup: 30,  gramsPerTbsp: 2 },
  espinacas:             { gramsPerCup: 30,  gramsPerTbsp: 2 },
  spinach:               { gramsPerCup: 30,  gramsPerTbsp: 2 },
  tomate:                { gramsPerCup: 180, gramsPerTbsp: 11.3 },
  tomato:                { gramsPerCup: 180, gramsPerTbsp: 11.3 },
  jitomate:              { gramsPerCup: 180, gramsPerTbsp: 11.3 },
  cebolla:               { gramsPerCup: 160, gramsPerTbsp: 10 },
  onion:                 { gramsPerCup: 160, gramsPerTbsp: 10 },
  ajo:                   { gramsPerCup: 136, gramsPerTbsp: 8.5 },
  garlic:                { gramsPerCup: 136, gramsPerTbsp: 8.5 },
  zanahoria:             { gramsPerCup: 128, gramsPerTbsp: 8 },
  carrot:                { gramsPerCup: 128, gramsPerTbsp: 8 },
  calabaza:              { gramsPerCup: 116, gramsPerTbsp: 7.3 },
  pumpkin:               { gramsPerCup: 116, gramsPerTbsp: 7.3 },
  calabacita:            { gramsPerCup: 124, gramsPerTbsp: 7.8 },
  zucchini:              { gramsPerCup: 124, gramsPerTbsp: 7.8 },
  pepino:                { gramsPerCup: 119, gramsPerTbsp: 7.4 },
  cucumber:              { gramsPerCup: 119, gramsPerTbsp: 7.4 },
  pimiento:              { gramsPerCup: 149, gramsPerTbsp: 9.3 },
  pimientos:             { gramsPerCup: 149, gramsPerTbsp: 9.3 },
  "bell pepper":         { gramsPerCup: 149, gramsPerTbsp: 9.3 },
  champiñones:           { gramsPerCup: 70,  gramsPerTbsp: 4.4 },
  mushrooms:             { gramsPerCup: 70,  gramsPerTbsp: 4.4 },
  hongos:                { gramsPerCup: 70,  gramsPerTbsp: 4.4 },
  brócoli:               { gramsPerCup: 91,  gramsPerTbsp: 5.7 },
  broccoli:              { gramsPerCup: 91,  gramsPerTbsp: 5.7 },
  coliflor:              { gramsPerCup: 100, gramsPerTbsp: 6.3 },
  cauliflower:           { gramsPerCup: 100, gramsPerTbsp: 6.3 },
  elote:                 { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  maíz:                  { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  corn:                  { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  papa:                  { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  potato:                { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  camote:                { gramsPerCup: 133, gramsPerTbsp: 8.3 },
  "sweet potato":        { gramsPerCup: 133, gramsPerTbsp: 8.3 },
  batata:                { gramsPerCup: 133, gramsPerTbsp: 8.3 },
  boniato:               { gramsPerCup: 133, gramsPerTbsp: 8.3 },
  yuca:                  { gramsPerCup: 165, gramsPerTbsp: 10.3 },
  cassava:               { gramsPerCup: 165, gramsPerTbsp: 10.3 },

  // ── Fruits ──
  fresa:                 { gramsPerCup: 152, gramsPerTbsp: 9.5 },
  fresas:                { gramsPerCup: 152, gramsPerTbsp: 9.5 },
  strawberries:          { gramsPerCup: 152, gramsPerTbsp: 9.5 },
  arándano:              { gramsPerCup: 148, gramsPerTbsp: 9.3 },
  blueberries:           { gramsPerCup: 148, gramsPerTbsp: 9.3 },
  frambuesa:             { gramsPerCup: 123, gramsPerTbsp: 7.7 },
  frambuesas:            { gramsPerCup: 123, gramsPerTbsp: 7.7 },
  raspberries:           { gramsPerCup: 123, gramsPerTbsp: 7.7 },
  manzana:               { gramsPerCup: 125, gramsPerTbsp: 7.8 },
  apple:                 { gramsPerCup: 125, gramsPerTbsp: 7.8 },
  plátano:               { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  platano:               { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  banana:                { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  mango:                 { gramsPerCup: 165, gramsPerTbsp: 10.3 },
  piña:                  { gramsPerCup: 165, gramsPerTbsp: 10.3 },
  pineapple:             { gramsPerCup: 165, gramsPerTbsp: 10.3 },
  naranja:               { gramsPerCup: 180, gramsPerTbsp: 11.3 },
  orange:                { gramsPerCup: 180, gramsPerTbsp: 11.3 },
  durazno:               { gramsPerCup: 154, gramsPerTbsp: 9.6 },
  peach:                 { gramsPerCup: 154, gramsPerTbsp: 9.6 },
  aguacate:              { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  avocado:               { gramsPerCup: 150, gramsPerTbsp: 9.4 },

  // ── Spices & seasonings (small amounts) ──
  sal:                   { gramsPerCup: 288, gramsPerTbsp: 18 },
  salt:                  { gramsPerCup: 288, gramsPerTbsp: 18 },
  canela:                { gramsPerCup: 125, gramsPerTbsp: 7.8 },
  cinnamon:              { gramsPerCup: 125, gramsPerTbsp: 7.8 },
  pimienta:              { gramsPerCup: 116, gramsPerTbsp: 7.3 },
  pepper:                { gramsPerCup: 116, gramsPerTbsp: 7.3 },
  comino:                { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  cumin:                 { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  jengibre:              { gramsPerCup: 96,  gramsPerTbsp: 6 },
  ginger:                { gramsPerCup: 96,  gramsPerTbsp: 6 },
  cúrcuma:               { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  turmeric:              { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  paprika:               { gramsPerCup: 110, gramsPerTbsp: 6.9 },
  pimentón:              { gramsPerCup: 110, gramsPerTbsp: 6.9 },
  "chile en polvo":      { gramsPerCup: 115, gramsPerTbsp: 7.2 },
  "chili powder":        { gramsPerCup: 115, gramsPerTbsp: 7.2 },
  orégano:               { gramsPerCup: 20,  gramsPerTbsp: 1.3 },
  oregano:               { gramsPerCup: 20,  gramsPerTbsp: 1.3 },
  albahaca:              { gramsPerCup: 24,  gramsPerTbsp: 1.5 },
  basil:                 { gramsPerCup: 24,  gramsPerTbsp: 1.5 },
  cilantro:              { gramsPerCup: 16,  gramsPerTbsp: 1 },
  perejil:               { gramsPerCup: 30,  gramsPerTbsp: 1.9 },
  parsley:               { gramsPerCup: 30,  gramsPerTbsp: 1.9 },
  romero:                { gramsPerCup: 20,  gramsPerTbsp: 1.3 },
  rosemary:              { gramsPerCup: 20,  gramsPerTbsp: 1.3 },
  tomillo:               { gramsPerCup: 18,  gramsPerTbsp: 1.1 },
  thyme:                 { gramsPerCup: 18,  gramsPerTbsp: 1.1 },

  // ── Baking ──
  "polvo para hornear":  { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "baking powder":       { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "bicarbonato":         { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "baking soda":         { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  levadura:              { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  yeast:                 { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  gelatina:              { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  gelatin:               { gramsPerCup: 150, gramsPerTbsp: 9.4 },

  // ── Proteins ──
  "proteína en polvo":   { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  "protein powder":      { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  colágeno:              { gramsPerCup: 110, gramsPerTbsp: 6.9 },
  collagen:              { gramsPerCup: 110, gramsPerTbsp: 6.9 },

  // ── Sauces & condiments ──
  "salsa de soya":       { gramsPerCup: 255, gramsPerTbsp: 16 },
  "soy sauce":           { gramsPerCup: 255, gramsPerTbsp: 16 },
  vinagre:               { gramsPerCup: 240, gramsPerTbsp: 15 },
  vinegar:               { gramsPerCup: 240, gramsPerTbsp: 15 },
  mostaza:               { gramsPerCup: 250, gramsPerTbsp: 15.6 },
  mustard:               { gramsPerCup: 250, gramsPerTbsp: 15.6 },
  mayonesa:              { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  mayonnaise:            { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "pasta de tomate":     { gramsPerCup: 262, gramsPerTbsp: 16.4 },
  "tomato paste":        { gramsPerCup: 262, gramsPerTbsp: 16.4 },

  // ── Liquids ──
  agua:                  { gramsPerCup: 240, gramsPerTbsp: 15 },
  water:                 { gramsPerCup: 240, gramsPerTbsp: 15 },
  caldo:                 { gramsPerCup: 240, gramsPerTbsp: 15 },
  broth:                 { gramsPerCup: 240, gramsPerTbsp: 15 },
  café:                  { gramsPerCup: 240, gramsPerTbsp: 15 },
  coffee:                { gramsPerCup: 240, gramsPerTbsp: 15 },
  jugo:                  { gramsPerCup: 248, gramsPerTbsp: 15.5 },
  juice:                 { gramsPerCup: 248, gramsPerTbsp: 15.5 },
  vino:                  { gramsPerCup: 236, gramsPerTbsp: 14.8 },
  wine:                  { gramsPerCup: 236, gramsPerTbsp: 14.8 },

  // ── Misc ──
  "extracto de vainilla": { gramsPerCup: 208, gramsPerTbsp: 13 },
  "vanilla extract":     { gramsPerCup: 208, gramsPerTbsp: 13 },
  vainilla:              { gramsPerCup: 208, gramsPerTbsp: 13 },
  vanilla:               { gramsPerCup: 208, gramsPerTbsp: 13 },
};

const GENERIC: ConversionEntry = { gramsPerCup: 140, gramsPerTbsp: 9 };

const GRAMS_PER_OUNCE = 28.3495;
const GRAMS_PER_POUND = 453.592;
const OUNCES_PER_POUND = 16;
const TSP_PER_TBSP = 3;

function findDensity(ingredientText: string): ConversionEntry {
  const lower = ingredientText.toLowerCase();
  // Try longer keys first (more specific matches)
  const sortedKeys = Object.keys(INGREDIENT_DENSITIES).sort((a, b) => b.length - a.length);
  for (const key of sortedKeys) {
    if (lower.includes(key)) return INGREDIENT_DENSITIES[key];
  }
  return GENERIC;
}

/**
 * The fractions each measuring tool actually has markings for. They differ:
 * a dry-measure cup set runs in thirds as well as quarters, a spoon set does
 * not — "⅓ tsp" is a number, not a measurement anyone can take. An eighth is
 * fine on a spoon (every set has ⅛ tsp) and hopeless in a cup, which is where
 * the "1 ⅛ cups" that triggered a refund came from.
 */
const CUP_FRACTIONS: [number, string][] = [
  [0.25, "¼"],
  [1 / 3, "⅓"],
  [0.5, "½"],
  [2 / 3, "⅔"],
  [0.75, "¾"],
];
const SPOON_FRACTIONS: [number, string][] = [
  [0.125, "⅛"],
  [0.25, "¼"],
  [0.5, "½"],
  [0.75, "¾"],
];

/** How far a value may sit from a real marking before the conversion stops
 *  being worth making. Past this, the gram figure it came from is the honest
 *  answer, so the caller keeps the weight. */
const SNAP_TOLERANCE = 0.15;

type MeasureTool = "cup" | "spoon";

/**
 * Format a quantity as whole numbers and markings that exist on the tool, or
 * return null when nothing on that tool is close enough to be worth printing.
 */
function formatFraction(n: number, tool: MeasureTool): string | null {
  if (!(n > 0)) return null;
  const whole = Math.floor(n);
  const fractions = tool === "cup" ? CUP_FRACTIONS : SPOON_FRACTIONS;

  const candidates: [number, string][] = [
    [whole, `${whole}`],
    [whole + 1, `${whole + 1}`],
    ...fractions.map(
      ([value, glyph]) =>
        [whole + value, whole > 0 ? `${whole} ${glyph}` : glyph] as [number, string],
    ),
  ];

  let best: string | null = null;
  let bestError = Infinity;
  for (const [value, label] of candidates) {
    if (value <= 0) continue; // "0 cups" is not a measurement
    const error = Math.abs(value - n) / n;
    if (error < bestError) {
      bestError = error;
      best = label;
    }
  }
  return bestError <= SNAP_TOLERANCE ? best : null;
}

/**
 * Ingredients that come in irregular lumps. A cup of these is whatever the
 * pieces happen to pack into, so a gram→cup figure is a false precision: the
 * "1 ⅛ cups" of chopped chocolate a customer complained about could be 150 g
 * or 250 g in practice. For these we keep the weight and let the ounces
 * option in the unit selector serve anyone who wants imperial.
 */
const IRREGULAR_SOLID_MARKERS = [
  // English
  "chopped", "chunks", "chunk", "grated", "shredded", "cubed", "cubes", "diced",
  "sliced", "slices", "pieces", "chips", "crumbled", "torn", "halved", "quartered",
  "whole", "crushed", "flakes",
  // Spanish
  "picado", "picada", "troceado", "troceada", "trozos", "rallado", "rallada",
  "cubos", "cortado", "cortada", "rodajas", "laminado", "machacado", "entero",
  "entera", "trituradas", "trituado",
  // German
  "gehackt", "gerieben", "gewürfelt", "geschnitten", "stücke", "zerbröselt", "ganze",
];

function isIrregularSolid(ingredientText: string): boolean {
  const lower = ingredientText.toLowerCase();
  return IRREGULAR_SOLID_MARKERS.some((marker) => lower.includes(marker));
}

/**
 * Detect grams/kilograms in ingredient text and convert to target unit.
 */
export function convertIngredient(text: string, targetUnit: MeasureUnit): string {
  if (targetUnit === "metric") return text;

  // First normalize kilograms to grams so the rest of the pipeline only deals with grams.
  // Match kg / kgs / kilo / kilos / kilogramo / kilograms — but NOT "g" alone.
  const kgRegex = /(\d+(?:[.,]\d+)?)\s*(?:kg|kgs|kilogramos?|kilograms?|kilos?)\b/gi;
  const normalized = text.replace(kgRegex, (_match, numStr) => {
    const kg = parseFloat(numStr.replace(",", "."));
    if (isNaN(kg) || kg <= 0) return _match;
    const grams = kg * 1000;
    return `${grams % 1 === 0 ? grams : grams.toFixed(1)} g`;
  });

  const gramsRegex = /(\d+(?:[.,]\d+)?)\s*(?:gr(?:amos?)?|grams?|g)\b/gi;

  return normalized.replace(gramsRegex, (match, numStr) => {
    const grams = parseFloat(numStr.replace(",", "."));
    if (isNaN(grams) || grams <= 0) return match;

    const density = findDensity(text);

    switch (targetUnit) {
      case "ounces": {
        // Under a quarter ounce the imperial figure is less usable than the
        // gram figure it came from ("⅛ oz salt"), so keep the weight as-is.
        if (grams < GRAMS_PER_OUNCE / 4) return match;
        // ≥ 1 lb (≈454 g) → use pounds (with oz remainder if not clean)
        if (grams >= GRAMS_PER_POUND) {
          const totalOz = grams / GRAMS_PER_OUNCE;
          const lbs = totalOz / OUNCES_PER_POUND;
          // Round to 0.25 lb steps for cleanliness
          const roundedLbs = Math.round(lbs * 4) / 4;
          return roundedLbs % 1 === 0
            ? `${roundedLbs} lb`
            : `${roundedLbs} lb`;
        }
        const oz = grams / GRAMS_PER_OUNCE;
        return `${Math.round(oz * 10) / 10} oz`;
      }
      case "cups": {
        // Lumpy solids have no honest cup figure — keep the weight.
        if (isIrregularSolid(text)) return match;
        const cups = grams / density.gramsPerCup;
        if (cups < 0.2) {
          const tbsp = grams / density.gramsPerTbsp;
          if (tbsp < 1) {
            const tsp = formatFraction(tbsp * TSP_PER_TBSP, "spoon");
            return tsp ? `${tsp} tsp` : match;
          }
          const spoons = formatFraction(tbsp, "spoon");
          return spoons ? `${spoons} tbsp` : match;
        }
        const measured = formatFraction(cups, "cup");
        return measured ? `${measured} cup${cups > 1.1 ? "s" : ""}` : match;
      }
      default:
        return match;
    }
  });
}

/** Parse a numeric string: integer, decimal, simple fraction "1/2", or mixed number "1 1/2" */
/**
 * Frações unicode como número. Copiado em espírito do Bread Again, onde o
 * mesmo levantamento achou UMA linha presa em vez de 3.973: lá o parser trata
 * "¼" como cidadão de primeira classe, então nenhum padrão precisa saber que
 * ela existe.
 *
 * A escolha aqui é a mesma, e é de propósito: a fração entra no PARSER e no
 * grupo de captura (QTY, abaixo), não numa reescrita do texto antes dos
 * padrões. Reescrever "¼ cucharadita" para "1/4 cucharadita" mudaria a saída
 * de linhas que hoje já saem certas — "¼ tsp" viraria "1/4 tsp" — e a regra
 * era não mexer no que já converte.
 */
const UNICODE_FRACTIONS: Record<string, number> = {
  "½": 0.5, "⅓": 1 / 3, "⅔": 2 / 3, "¼": 0.25, "¾": 0.75,
  "⅕": 0.2, "⅖": 0.4, "⅗": 0.6, "⅘": 0.8, "⅙": 1 / 6, "⅚": 5 / 6,
  "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875,
};
const GLIFOS = "½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞";

/**
 * O grupo que captura a quantidade, um só para todos os padrões: inteiro,
 * decimal, fração ASCII, mista ASCII, fração unicode e mista unicode ("2¼").
 */
const QTY = String.raw`(?:\d+\s*[${GLIFOS}]|[${GLIFOS}]|(?:\d+\s+)?\d+\/\d+|\d+(?:[.,]\d+)?)`;

function parseNum(s: string): number {
  const t = s.trim();

  // Mista unicode: "2¼"
  const mistaUni = t.match(new RegExp(String.raw`^(\d+)\s*([${GLIFOS}])$`));
  if (mistaUni) return parseInt(mistaUni[1], 10) + UNICODE_FRACTIONS[mistaUni[2]];

  // Fração unicode sozinha: "¼"
  if (UNICODE_FRACTIONS[t] !== undefined) return UNICODE_FRACTIONS[t];

  // Mixed number: "1 1/2" → 1.5
  const mixed = t.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const den = parseInt(mixed[3], 10);
    return den !== 0 ? parseInt(mixed[1], 10) + parseInt(mixed[2], 10) / den : NaN;
  }
  if (t.includes("/")) {
    const [a, b] = t.split("/").map(Number);
    return b !== 0 ? a / b : NaN;
  }
  return parseFloat(t.replace(",", "."));
}

/**
 * Normalize English unit strings produced by translateSpanishUnits before pattern matching.
 * "cup(s)" → "cups",  "glass(es)" → "glasses"  (the literal parentheses break \b assertions)
 */
function normalizeEnglishUnits(text: string): string {
  return text
    .replace(/cup\(s\)/gi, "cups")
    .replace(/glass\(es\)/gi, "glasses");
}

// [regex, ml per unit]
// Covers Spanish originals + English forms (abbreviated and full words)
// The capture group supports: plain integers (2), decimals (1.5), simple fractions (1/2),
// and mixed numbers (1 1/2) — e.g. "1 1/2 cucharada" was previously broken into
// orphaned "1" + converted "5.3 g" because only the fractional part was matched.
/**
 * Unidades de volume por nome, e quantos ml cada uma vale.
 *
 * O alemão entrou aqui depois de a varredura mostrar 8.830 linhas de
 * ingrediente presas — Tasse, EL, TL e Prise não tinham padrão nenhum, em
 * nenhum modo. Küchenglück, Brotglück e Kochglück têm a receita-base em
 * alemão, então a conversão simplesmente não existia para os três módulos.
 *
 * EL e TL exigem dígito ou fração antes por causa do espanhol: "el" é artigo.
 * Como QTY é obrigatório em todos os padrões, isso já está garantido.
 *
 * Prise / pizca / pinch não são volume mensurável: ficam de fora de propósito,
 * como a sal e as especiarias já ficavam.
 */
const SPANISH_VOLUME_PATTERNS: [RegExp, number][] = [
  [new RegExp(String.raw`(${QTY})\s*(?:tazas?|cups?|Tassen?)\b`, "gi"),                                     240],
  [new RegExp(String.raw`(${QTY})\s*(?:vasos?|glasses?|Gl(?:a|ä)ser?)\b`, "gi"),                             240],
  [new RegExp(String.raw`(${QTY})\s*(?:Becher)\b`, "g"),                                                     250],
  [new RegExp(String.raw`(${QTY})\s*(?:cucharadas?|cdas?\.?|tablespoons?|tbsp|EL)\b`, "g"),                 15],
  [new RegExp(String.raw`(${QTY})\s*(?:cucharadas?|cdas?\.?|tablespoons?|tbsp)\b`, "gi"),                   15],
  [new RegExp(String.raw`(${QTY})\s*(?:cucharaditas?|cdtas?\.?|teaspoons?|tsp|TL)\b`, "g"),                  5],
  [new RegExp(String.raw`(${QTY})\s*(?:cucharaditas?|cdtas?\.?|teaspoons?|tsp)\b`, "gi"),                    5],
];
/**
 * Par de medidas na mesma linha: "1½ cups (150 g)", "120g (½ cup)",
 * "3/4 Tasse Erdbeeren (ca. 90 g)".
 *
 * O parêntese é cortesia do autor no outro sistema, não um segundo
 * ingrediente. Converter os dois produz duas medidas da mesma coisa no mesmo
 * sistema — "12.2 fl oz (5.3 oz)" — que é pior do que não converter nada.
 *
 * É o mesmo molde do cWithFHint em convertTemperatures, que existe porque
 * "175°C (350°F)" virava 347°F: o número do autor ganha do número calculado.
 * Aqui a escolha é de lado, não de número. Fica o lado mais perto do alvo e o
 * outro sai da linha:
 *
 *   alvo metric  → fica o lado métrico     (g, kg, ml, l)
 *   alvo cups    → fica o lado volumétrico (cup, taza, Tasse, EL, TL…)
 *   alvo ounces  → fica o lado métrico, que converte limpo para oz; o
 *                  volumétrico só entra se não houver métrico
 */
const UNID_METRICA = String.raw`(?:g|gr|gramos?|grams?|kg|kilos?|ml|l|litros?|liters?)`;
const UNID_VOLUME = String.raw`(?:tazas?|cups?|Tassen?|vasos?|glasses?|Becher|cucharadas?|cdas?\.?|tablespoons?|tbsp|EL|cucharaditas?|cdtas?\.?|teaspoons?|tsp|TL)`;
const APROX = String.raw`(?:ca\.|aprox\.?|approx\.?|about|~|≈)?\s*`;

/** O sistema a que uma unidade pertence, para decidir qual lado fica. */
function sistemaDa(unidade: string): "metrica" | "volume" {
  return new RegExp(String.raw`^${UNID_METRICA}$`, "i").test(unidade.trim()) ? "metrica" : "volume";
}

export function resolveDualMeasure(text: string, targetUnit: MeasureUnit): string {
  // medida na cabeça da linha
  const cabeca = new RegExp(String.raw`^\s*(${QTY})\s*(${UNID_METRICA}|${UNID_VOLUME})\b`);
  const mCabeca = text.match(cabeca);
  if (!mCabeca) return text;

  // medida entre parênteses, em qualquer ponto
  const parenteses = new RegExp(String.raw`\s*\(\s*${APROX}(${QTY})\s*(${UNID_METRICA}|${UNID_VOLUME})\s*\)`);
  const mParen = text.match(parenteses);
  if (!mParen) return text;

  // Recusa o que nao entende, em vez de resolver metade.
  //
  //   "1 cup plus 2 tablespoons (255 g) ... 1 cup (226 g)"  dois pares: os
  //   255 g sao o total, nao a primeira parcela, e promover a cabeca escrevia
  //   "255 g plus 28 g" — uma quantidade que nao existe na receita.
  //
  //   "(about ¼ cup + 2 tbsp)" e "(4¼ oz / 120g)"  o parentese e uma soma ou
  //   uma lista, nao a cortesia do autor no outro sistema.
  const todosOsParenteses = text.match(new RegExp(parenteses.source, "g")) ?? [];
  if (todosOsParenteses.length !== 1) return text;
  // A barra so separa medidas quando tem espaco dos dois lados: "4¼ oz / 120g".
  // Sem espaco ela e fracao ASCII — "1 1/2 Tassen" — e a linha e valida.
  if (/\+|\s\/\s/.test(mParen[0])) return text;

  const sisCabeca = sistemaDa(mCabeca[2]);
  const sisParen = sistemaDa(mParen[2]);
  if (sisCabeca === sisParen) return text; // não é par de sistemas; não mexe

  const preferido = targetUnit === "cups" ? "volume" : "metrica";
  const ficaOParenteses = sisParen === preferido;

  const resto = text
    .replace(parenteses, "")
    .replace(cabeca, "")
    .replace(/^\s*(?:de\s+|of\s+)?/i, "")
    .trim();

  const vencedor = ficaOParenteses
    ? `${mParen[1]} ${mParen[2]}`
    : `${mCabeca[1]} ${mCabeca[2]}`;

  return `${vencedor} ${resto}`.trim();
}

/**
 * Convert Spanish volume units (taza, cucharada, cucharadita) → grams for metric mode.
 */
function convertSpanishToMetric(text: string): string {
  const normalized = normalizeEnglishUnits(text);
  let result = normalized;
  for (const [regex, mlPerUnit] of SPANISH_VOLUME_PATTERNS) {
    result = result.replace(regex, (match, numStr) => {
      const qty = parseNum(numStr);
      if (isNaN(qty) || qty <= 0) return match;
      const cups = (qty * mlPerUnit) / 240;
      const density = findDensity(normalized);
      const grams = cups * density.gramsPerCup;
      if (isNaN(grams) || grams <= 0) return match;
      return grams >= 10
        ? `${Math.round(grams)} g`
        : `${Math.round(grams * 10) / 10} g`;
    });
  }
  return result;
}

/**
 * Convert Spanish volume units (taza, cucharada, cucharadita, vaso) → fl oz for ounces mode.
 * In cups mode the Spanish units are kept as-is (taza/cucharada are already cup-based).
 */
function convertSpanishVolumes(text: string, targetUnit: MeasureUnit): string {
  if (targetUnit !== "ounces") return text;

  const normalized = normalizeEnglishUnits(text);
  let result = normalized;
  for (const [regex, mlPerUnit] of SPANISH_VOLUME_PATTERNS) {
    result = result.replace(regex, (match, numStr) => {
      const qty = parseNum(numStr);
      if (isNaN(qty) || qty <= 0) return match;
      const ml = qty * mlPerUnit;
      const flOz = ml / 29.5735;
      if (flOz < 0.03) return match; // truly negligible — keep original
      // Amounts < 0.5 fl oz (< ~1 tbsp) read better as tsp
      if (flOz < 0.5) {
        const tsp = formatFraction(ml / 4.92892, "spoon");
        return tsp ? `${tsp} tsp` : match;
      }
      return `${Math.round(flOz * 10) / 10} fl oz`;
    });
  }
  return result;
}

/** Also convert ml for liquids, and Spanish volume units (taza, cucharada, etc.) */
/**
 * A unidade impressa no vocabulário da tela.
 *
 * O conversor trabalha internamente em tokens ingleses — tsp, tbsp, cup —
 * porque é neles que as densidades e as frações estão escritas. Sem uma
 * passagem final, "1 TL (5 ml)" numa tela alemã saía "1 tsp": alemão entrando,
 * inglês saindo. Eram 365 linhas.
 *
 * Só o nome da unidade muda; o número já está certo quando chega aqui.
 */
const UNIDADE_NA_TELA: Record<string, [RegExp, string][]> = {
  de: [
    [/\bcups\b/g, "Tassen"],
    [/\bcup\b/g, "Tasse"],
    [/\btbsp\b/g, "EL"],
    [/\btsp\b/g, "TL"],
    [/\bpinch\b/gi, "Prise"],
  ],
  es: [
    [/\bcups\b/g, "tazas"],
    [/\bcup\b/g, "taza"],
    [/\btbsp\b/g, "cda"],
    [/\btsp\b/g, "cdta"],
    [/\bpinch\b/gi, "pizca"],
  ],
};

export function renderUnitsIn(text: string, language: string): string {
  const tabela = UNIDADE_NA_TELA[language];
  if (!tabela) return text;
  let out = text;
  for (const [re, nome] of tabela) out = out.replace(re, nome);
  return out;
}

export function convertIngredientFull(
  text: string,
  targetUnit: MeasureUnit,
  language = "en",
): string {
  // Antes de qualquer padrão: se a linha traz o par do autor — "1½ cups
  // (150 g)" — fica um lado só. Converter os dois dava duas medidas da mesma
  // coisa no mesmo sistema.
  const entrada = resolveDualMeasure(text, targetUnit);

  if (targetUnit === "metric") {
    // Convert Spanish volumetric units → grams so metric mode shows actual weights
    return renderUnitsIn(convertSpanishToMetric(entrada), language);
  }

  let result = convertIngredient(entrada, targetUnit);

  // Normalize liters to ml first so downstream patterns can match.
  // Matches: 1 l, 1L, 1 litro, 1 liter, 1 litros, 1 liters
  const litersRegex = /(\d+(?:[.,]\d+)?)\s*(?:l|litros?|liters?)\b/gi;
  result = result.replace(litersRegex, (match, numStr) => {
    const l = parseFloat(numStr.replace(",", "."));
    if (isNaN(l) || l <= 0) return match;
    const ml = l * 1000;
    return `${ml % 1 === 0 ? ml : ml.toFixed(0)} ml`;
  });

  const mlRegex = /(\d+(?:[.,]\d+)?)\s*ml\b/gi;
  result = result.replace(mlRegex, (match, numStr) => {
    const ml = parseFloat(numStr.replace(",", "."));
    if (isNaN(ml) || ml <= 0) return match;

    switch (targetUnit) {
      case "ounces": {
        const flOz = ml / 29.5735;
        return `${Math.round(flOz * 10) / 10} fl oz`;
      }
      case "cups": {
        const cups = ml / 240;
        if (cups < 0.2) {
          const tbsp = ml / 15;
          if (tbsp < 1) {
            const tsp = formatFraction(tbsp * TSP_PER_TBSP, "spoon");
            return tsp ? `${tsp} tsp` : match;
          }
          const spoons = formatFraction(tbsp, "spoon");
          return spoons ? `${spoons} tbsp` : match;
        }
        const measured = formatFraction(cups, "cup");
        return measured ? `${measured} cup${cups > 1.1 ? "s" : ""}` : match;
      }
      default:
        return match;
    }
  });

  // Convert Spanish volume units (taza → fl oz, cucharada → tbsp/fl oz, etc.)
  result = convertSpanishVolumes(result, targetUnit);

  // In cups mode, clean up any "cup(s)"/"glass(es)" artifacts left by translateSpanishUnits
  if (targetUnit === "cups") {
    result = normalizeEnglishUnits(result);
  }

  return renderUnitsIn(result, language);
}

// ─── Spanish → English unit name translation ───

const SPANISH_UNIT_MAP: [RegExp, string][] = [
  [/\bcucharaditas?\b/gi, "tsp"],
  [/\bcdtas?\b\.?/gi, "tsp"],
  [/\bcucharadas?\b/gi, "tbsp"],
  [/\bcdas?\b\.?/gi, "tbsp"],
  [/\btazas?\b/gi, "cup(s)"],
  [/\bpiezas?\b/gi, "piece(s)"],
  [/\bpizca\b/gi, "pinch"],
  [/\brebanadas?\b/gi, "slice(s)"],
  [/\bdientes?\s+de\s+ajo\b/gi, "garlic clove(s)"],
  [/\bpuñado\b/gi, "handful"],
  [/\brajitas?\b/gi, "strip(s)"],
  [/\blitros?\b/gi, "liter(s)"],
  [/\bvasos?\b/gi, "glass(es)"],
  [/\bal\s+gusto\b/gi, "to taste"],
];

/**
 * Translate Spanish measurement unit names to English equivalents.
 * Only call this for EN-language users to avoid Spanish leaking into the UI.
 */
/**
 * Os mesmos nomes em alemão.
 *
 * A varredura achou 301 linhas com unidade espanhola dentro de linha alemã —
 * e o caminho de tradução só existia para o inglês, então elas chegavam
 * intactas na tela: "2 cda Kakaopulver". Mandar "tbsp" para uma tela alemã
 * trocaria um idioma errado por outro, então aqui a tabela é ES→DE, não
 * ES→EN.
 */
const GERMAN_UNIT_MAP: [RegExp, string][] = [
  [/\bcucharaditas?\b/gi, "TL"],
  [/\bcdtas?\b\.?/gi, "TL"],
  [/\bcucharadas?\b/gi, "EL"],
  [/\bcdas?\b\.?/gi, "EL"],
  [/\btazas?\b/gi, "Tasse(n)"],
  [/\bpiezas?\b/gi, "Stück"],
  [/\bpizca\b/gi, "Prise"],
  [/\brebanadas?\b/gi, "Scheibe(n)"],
  [/\bdientes?\s+de\s+ajo\b/gi, "Knoblauchzehe(n)"],
  [/\bpuñado\b/gi, "Handvoll"],
  [/\blitros?\b/gi, "Liter"],
  [/\bvasos?\b/gi, "Glas/Gläser"],
  [/\bal\s+gusto\b/gi, "nach Geschmack"],
];

/**
 * Nome de unidade espanhola traduzido para o idioma da tela.
 *
 * Idioma sem tabela — o próprio espanhol — devolve o texto intacto.
 */
export function translateUnitsTo(text: string, language: string): string {
  const tabela = language === "en" ? SPANISH_UNIT_MAP
               : language === "de" ? GERMAN_UNIT_MAP
               : null;
  if (!tabela) return text;
  let result = text;
  for (const [regex, replacement] of tabela) {
    result = result.replace(regex, replacement);
  }
  return result;
}

/** Atalho para o inglês. Mantido porque é o nome que os chamadores já usam. */
export function translateSpanishUnits(text: string): string {
  return translateUnitsTo(text, "en");
}

/**
 * Convert temperatures bidirectionally:
 *   - imperial mode (cups / ounces): °C → °F
 *   - metric  mode:                  °F → °C
 *
 * Matched patterns (case-insensitive, optional °, optional "degrees"/"grados"):
 *   180°C · 180 °C · 180° C · 180 grados C · 180 grados Celsius · 180C
 *   350°F · 350 °F · 350 F · 350 degrees Fahrenheit · 350F
 *
 * The unit letter must NOT be followed by another letter so we don't capture
 * things like "Cilantro" or "Flour".
 */
// Below this, a temperature is not an oven setting — proofing, melting
// chocolate, warming milk, serving temperature — and the exact figure matters
// more than landing on a dial marking.
const OVEN_MIN_C = 120;
const OVEN_MIN_F = 250;

/** Oven dials are marked every 25°F, so that is the only precision a reader
 *  can act on. 175°C is 347°F on paper and 350°F on every oven in the world. */
function snapOvenF(exactF: number): number {
  if (exactF < OVEN_MIN_F) return Math.round(exactF);
  return Math.round(exactF / 25) * 25;
}

/** Same idea going the other way: metric ovens are marked in 5°C steps. */
function snapOvenC(exactC: number): number {
  if (exactC < OVEN_MIN_C) return Math.round(exactC);
  return Math.round(exactC / 5) * 5;
}

export function convertTemperatures(text: string, targetUnit: MeasureUnit): string {
  // Step 0 — Dual-unit pairs: "X°C (Y°F)" or "X°F (Y°C)"
  // Recipes often include the other-system value in parentheses as a courtesy.
  // Both values are captured so that whichever one matches the target unit can
  // be used verbatim: the recipe author's own number always beats a computed
  // one. "175°C (350°F)" used to be recomputed into 347°F, throwing away the
  // correct 350°F sitting right there in the source.
  // The degree sign is optional on both halves: recipe text in the wild writes
  // "175C (350F)" as often as "175°C (350°F)", and requiring the symbol left
  // the parenthesis untouched — printing "Preheat oven to 350°F (350F)".
  const cWithFHint = /(\d+(?:[.,]\d+)?)\s*°?\s*C\s*\(\s*(\d+(?:[.,]\d+)?)\s*°?\s*F\s*\)/gi;
  const fWithCHint = /(\d+(?:[.,]\d+)?)\s*°?\s*F\s*\(\s*(\d+(?:[.,]\d+)?)\s*°?\s*C\s*\)/gi;

  if (targetUnit === "metric") {
    text = text
      // "180°C (350°F)" → "180°C"  (already in C, drop F hint)
      .replace(cWithFHint, (_match, c) => `${c}°C`)
      // "350°F (180°C)" → "180°C"  (use the author's C, drop F)
      .replace(fWithCHint, (_match, _f, c) => `${c}°C`);
  } else {
    // "175°C (350°F)" → "350°F"  (use the author's F, drop C)
    text = text.replace(cWithFHint, (_match, _c, f) => `${f}°F`);
    // "350°F (180°C)" → "350°F"  (already in F, drop C hint)
    text = text.replace(fWithCHint, (_match, f) => `${f}°F`);
  }

  // Step 1 — Range patterns like "150–165°C" or "300–325°F"
  const celsiusRangeRegex = /(\d+(?:[.,]\d+)?)\s*[–\-]\s*(\d+(?:[.,]\d+)?)\s*°?\s*(?:grados?|degrees?)?\s*(?:c(?:elsius)?)(?![a-z])/gi;
  const fahrenheitRangeRegex = /(\d+(?:[.,]\d+)?)\s*[–\-]\s*(\d+(?:[.,]\d+)?)\s*°?\s*(?:grados?|degrees?)?\s*(?:f(?:ahrenheit)?)(?![a-z])/gi;
  // Step 2 — Single values
  const celsiusRegex = /(\d+(?:[.,]\d+)?)\s*°?\s*(?:grados?|degrees?)?\s*(?:c(?:elsius)?)(?![a-z])/gi;
  const fahrenheitRegex = /(\d+(?:[.,]\d+)?)\s*°?\s*(?:grados?|degrees?)?\s*(?:f(?:ahrenheit)?)(?![a-z])/gi;

  if (targetUnit === "metric") {
    // F → C: ranges first, then singles
    return text
      .replace(fahrenheitRangeRegex, (match, n1, n2) => {
        const f1 = parseFloat(n1.replace(",", "."));
        const f2 = parseFloat(n2.replace(",", "."));
        if (isNaN(f1) || isNaN(f2)) return match;
        return `${snapOvenC((f1 - 32) * 5 / 9)}–${snapOvenC((f2 - 32) * 5 / 9)}°C`;
      })
      .replace(fahrenheitRegex, (match, numStr) => {
        const fahrenheit = parseFloat(numStr.replace(",", "."));
        if (isNaN(fahrenheit)) return match;
        return `${snapOvenC((fahrenheit - 32) * 5 / 9)}°C`;
      });
  }

  // Imperial modes (cups, ounces) — C → F: ranges first, then singles
  return text
    .replace(celsiusRangeRegex, (match, n1, n2) => {
      const c1 = parseFloat(n1.replace(",", "."));
      const c2 = parseFloat(n2.replace(",", "."));
      if (isNaN(c1) || isNaN(c2)) return match;
      return `${snapOvenF(c1 * 9 / 5 + 32)}–${snapOvenF(c2 * 9 / 5 + 32)}°F`;
    })
    .replace(celsiusRegex, (match, numStr) => {
      const celsius = parseFloat(numStr.replace(",", "."));
      if (isNaN(celsius)) return match;
      return `${snapOvenF(celsius * 9 / 5 + 32)}°F`;
    });
}

// ─── Pan / tin / thickness dimensions inside step text ───
// These live in the body of a step ("Line a 20×20 cm baking pan"), never in
// the ingredient list, so the ingredient pipeline never saw them and an
// imperial reader was left with centimetres.

const CM_PER_INCH = 2.54;
const NUM = String.raw`\d+(?:[.,]\d+)?`;
/** One measurement, or several joined by × / x: "20", "20×20", "9 x 13" */
const DIMS = String.raw`${NUM}(?:\s*[x×]\s*${NUM})*`;

/**
 * "a 20×20 cm pan" becomes "a 8×8 inch pan" unless the article is fixed —
 * English picks a/an by sound, and 8, 11 and 18 all begin with a vowel sound.
 */
function fixArticles(text: string): string {
  const vowelSounding = (n: string) => /^(8|11|18|80|81|82|83|84|85|86|87|88|89)\b/.test(n);
  return text
    .replace(/\ba (\d+)/g, (m, n) => (vowelSounding(n) ? `an ${n}` : m))
    .replace(/\ban (\d+)/g, (m, n) => (vowelSounding(n) ? m : `a ${n}`));
}

function mapDims(dims: string, convert: (n: number) => string): string {
  return dims
    .split(/\s*[x×]\s*/)
    .map((part) => {
      const n = parseFloat(part.replace(",", "."));
      return isNaN(n) ? part : convert(n);
    })
    .join(dims.includes("×") ? "×" : "x");
}

/** Bakeware is sold in whole and half inches — 7.87 in is an 8 in pan. */
const cmToInch = (cm: number) => String(Math.round((cm / CM_PER_INCH) * 2) / 2);
const inchToCm = (inch: number) => String(Math.round(inch * CM_PER_INCH));

/**
 * Convert pan sizes, thicknesses and diameters written into step text.
 *
 * As with temperatures, a dual-unit pair already in the source wins over
 * anything we compute: "8x8 inch (20x20 cm)" keeps whichever half matches the
 * reader's unit. Only "inch"/"inches"/quote are recognised going metric —
 * a bare "in" is ordinary English ("cut 3 in half") and converting it would
 * mangle the sentence.
 */
export function convertDimensions(text: string, targetUnit: MeasureUnit): string {
  const inchWithCmHint = new RegExp(
    String.raw`(${DIMS})\s*(?:inches|inch|in|")\s*\(\s*(${DIMS})\s*(?:cm|centimeters?|centímetros?)\s*\)`,
    "gi",
  );
  const cmWithInchHint = new RegExp(
    String.raw`(${DIMS})\s*(?:cm|centimeters?|centímetros?)\s*\(\s*(${DIMS})\s*(?:inches|inch|in|")\s*\)`,
    "gi",
  );

  if (targetUnit === "metric") {
    let result = text
      .replace(inchWithCmHint, (_m, _imp, met) => `${met} cm`)
      .replace(cmWithInchHint, (_m, met) => `${met} cm`);
    const inchRegex = new RegExp(String.raw`(${DIMS})\s*(?:inches|inch|")`, "gi");
    return fixArticles(result.replace(inchRegex, (_m, dims) => `${mapDims(dims, inchToCm)} cm`));
  }

  let result = text
    .replace(inchWithCmHint, (_m, imp) => `${imp} inch`)
    .replace(cmWithInchHint, (_m, _met, imp) => `${imp} inch`);
  const cmRegex = new RegExp(String.raw`(${DIMS})\s*(?:cm|centimeters?|centímetros?)\b`, "gi");
  return fixArticles(result.replace(cmRegex, (_m, dims) => `${mapDims(dims, cmToInch)} inch`));
}
